import { useEffect, useState } from 'react';
import { useAuth, hasFullAccess } from '../context/AuthContext';
import { useLang, LANG_OPTIONS } from '../context/LanguageContext';
import { useHelpGuide } from '../context/HelpGuideContext';
import { useWhatsAppHelp } from '../context/WhatsAppHelpContext';
import { useSubscriptionGate } from '../context/SubscriptionGateContext';
import TextSizeToggle from '../components/TextSizeToggle';
import { goNativeTab, useNativeTab } from './NativeTabContext';
import NativeInfoSheet from './NativeInfoSheet';
import { SITE_URL } from '../config/env.js';
import { shareLink } from '../utils/nativeShare';
import {
  getEditionAlertsEnabled,
  enableEditionAlerts,
  disableEditionAlerts,
} from '../utils/editionAlerts';
import { listBookmarks, removeBookmark } from '../utils/bookmarks';
import { pickTeEn } from '../utils/localizedContent.js';
import { mediaUrl } from '../config/env.js';
import './NativeYouScreen.css';

function Chevron() {
  return (
    <svg className="toi-menu__chev" viewBox="0 0 12 20" width="8" height="14" aria-hidden>
      <path d="M2.2 2.2L9.8 10 2.2 17.8" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Row({ onClick, children, danger }) {
  return (
    <button type="button" className={`toi-menu__row${danger ? ' is-danger' : ''}`} onClick={onClick}>
      <span>{children}</span>
      {!danger ? <Chevron /> : null}
    </button>
  );
}

const LOCALE_MAP = { te: 'te-IN', en: 'en-IN', ta: 'ta-IN', kn: 'kn-IN' };

function toMs(value) {
  if (value == null || value === '') return 0;
  const n = Number(value);
  if (Number.isFinite(n) && n > 1e11) return n;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function passCaption(user, member, t, lang) {
  if (!member) return `₹999 · ${t.readDailyBtn}`;
  const trial = toMs(user?.trialUntil);
  const sub = toMs(user?.subscribedUntil);
  const until = trial > Date.now() ? trial : sub;
  if (until > Date.now()) {
    const date = new Date(until).toLocaleDateString(LOCALE_MAP[lang] || 'en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
    const prefix = trial > Date.now() ? t.nativeTrialUntil : t.nativeValidUntil;
    return `${prefix} ${date}`;
  }
  return t.nativePassActive;
}

export default function NativeYouScreen({ onOpenStory, onOpenSearch }) {
  const { user, logout } = useAuth();
  const { lang, switchLang, t } = useLang();
  const { tab } = useNativeTab();
  const { openHelpGuide } = useHelpGuide();
  const { openWhatsAppHelp } = useWhatsAppHelp();
  const { openSubscribe } = useSubscriptionGate();
  const [sheet, setSheet] = useState(null);
  const [alertsOn, setAlertsOn] = useState(() => getEditionAlertsEnabled());
  const [bookmarks, setBookmarks] = useState(() => listBookmarks());
  const openSheet = (id, title) => setSheet({ id, title });

  useEffect(() => {
    const refresh = () => setBookmarks(listBookmarks());
    refresh();
    window.addEventListener('rk-bookmarks-changed', refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener('rk-bookmarks-changed', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  useEffect(() => {
    if (tab === 'you') setBookmarks(listBookmarks());
  }, [tab]);

  const sheetTitles = {
    terms: t.footerTerms,
    privacy: t.footerPrivacy,
    refund: t.footerRefund,
    cancellation: t.footerCancellation,
    contact: t.contact,
  };
  const member = hasFullAccess(user);
  const display = user?.name || t.nativeGuest;
  const initial = String(display).trim().charAt(0).toUpperCase() || 'G';

  const openPass = () => {
    if (member) {
      goNativeTab('paper');
      return;
    }
    openSubscribe();
  };

  const shareApp = async () => {
    const url = SITE_URL || 'https://repatikosam.com';
    await shareLink({ title: t.brandName, text: t.referralShareText, url });
  };

  const toggleEditionAlerts = async () => {
    if (alertsOn) {
      await disableEditionAlerts();
      setAlertsOn(false);
      return;
    }
    const ok = await enableEditionAlerts(user?.token);
    setAlertsOn(!!ok || getEditionAlertsEnabled());
  };

  return (
    <div className="toi-menu">
      <div className="toi-menu__profile">
        <span className="toi-menu__avatar" aria-hidden>
          {initial}
        </span>
        <div>
          <strong>{display}</strong>
          <p>{user?.email || t.nativeGuestHint}</p>
          <em>{member ? t.nativeMember : t.nativeGuest}</em>
        </div>
      </div>

      <button type="button" className={`toi-pass${member ? ' is-member' : ''}`} onClick={openPass}>
        <span className="toi-pass__mark" aria-hidden>
          {member ? '✓' : '₹'}
        </span>
        <span className="toi-pass__copy">
          <strong>{t.subAnnualPass}</strong>
          <em>{passCaption(user, member, t, lang)}</em>
          {!member ? <span className="toi-pass__hint">{t.nativeUnlockHint}</span> : null}
        </span>
        {member ? (
          <span className="toi-pass__status">{t.nativePassActive}</span>
        ) : (
          <span className="toi-pass__get">{t.nativePassGet}</span>
        )}
      </button>

      <h2>{t.nativePrefs}</h2>
      <div className="toi-menu__group">
        <div className="toi-menu__pref">
          <p>{t.statLangLabel}</p>
          <div className="toi-menu__langs">
            {LANG_OPTIONS.map((l) => (
              <button
                key={l.code}
                type="button"
                className={lang === l.code ? 'is-on' : ''}
                onClick={() => switchLang(l.code)}
              >
                {l.short}
              </button>
            ))}
          </div>
        </div>
        <div className="toi-menu__pref toi-menu__pref--size">
          <p>{t.textSizeLabel}</p>
          <TextSizeToggle />
        </div>
        <button type="button" className="toi-menu__row toi-menu__row--toggle" onClick={toggleEditionAlerts}>
          <span>{t.nativeEditionAlerts}</span>
          <em className={`toi-menu__switch${alertsOn ? ' is-on' : ''}`} aria-hidden />
        </button>
        <Row onClick={onOpenSearch}>{t.nativeSearch}</Row>
      </div>

      {bookmarks.length > 0 ? (
        <>
          <h2>{t.nativeBookmarks}</h2>
          <div className="toi-menu__group toi-menu__bookmarks">
            {bookmarks.map((b) => {
              const title = pickTeEn(lang, b.title, b.titleEn) || t.brandName;
              const cover = b.cover ? mediaUrl(b.cover) : '';
              return (
                <div key={b.id} className="toi-bookmark">
                  <button
                    type="button"
                    className="toi-bookmark__open"
                    onClick={() => onOpenStory?.(b.id)}
                  >
                    {cover ? <img src={cover} alt="" /> : <span className="toi-bookmark__ph" aria-hidden />}
                    <strong>{title}</strong>
                  </button>
                  <button
                    type="button"
                    className="toi-bookmark__del"
                    aria-label={t.closeBtn}
                    onClick={() => {
                      removeBookmark(b.id);
                      setBookmarks(listBookmarks());
                    }}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        </>
      ) : null}

      <h2>{t.nativeSupport}</h2>
      <div className="toi-menu__group">
        <Row onClick={openHelpGuide}>{t.navHelp}</Row>
        <Row onClick={() => openWhatsAppHelp('you_tab')}>{t.navWhatsApp}</Row>
        <Row onClick={() => openSheet('contact', t.contact)}>{t.contact}</Row>
        <Row onClick={shareApp}>{t.nativeShareApp}</Row>
      </div>

      <h2>{t.nativeLegal}</h2>
      <div className="toi-menu__group">
        <Row onClick={() => openSheet('terms', t.footerTerms)}>{t.footerTerms}</Row>
        <Row onClick={() => openSheet('privacy', t.footerPrivacy)}>{t.footerPrivacy}</Row>
        <Row onClick={() => openSheet('refund', t.footerRefund)}>{t.footerRefund}</Row>
        <Row onClick={() => openSheet('cancellation', t.footerCancellation)}>
          {t.footerCancellation}
        </Row>
      </div>

      {user && (
        <div className="toi-menu__group toi-menu__group--out">
          <Row danger onClick={logout}>
            {t.logout}
          </Row>
        </div>
      )}

      {sheet ? (
        <NativeInfoSheet
          page={sheet.id}
          title={sheet.title}
          doneLabel={t.nativeDone}
          onClose={() => setSheet(null)}
          onOpenPage={(id) => openSheet(id, sheetTitles[id])}
        />
      ) : null}
    </div>
  );
}
